<script lang="ts" setup>
import { computed } from 'vue'
import type { HomePageConfig } from '../configs/types'
import ModuleSlideBook from './modules/ModuleSlideBook.vue'
import ModuleGrid2x4 from './modules/ModuleGrid2x4.vue'
import ModuleFeatured1Plus4 from './modules/ModuleFeatured1Plus4.vue'
import ModuleGrid1x4 from './modules/ModuleGrid1x4.vue'
import ModuleFeed from './modules/ModuleFeed.vue'
import ModuleTags2x3 from './modules/ModuleTags2x3.vue'
import ModuleRanking from './modules/ModuleRanking.vue'
import ModuleSwiper from './modules/ModuleSwiper.vue'
import { bannerShow } from '@/composables/useBannerShow'
import { COLUMN_TYPE } from '@/constants'

const props = defineProps<{
  config: HomePageConfig
  setInfiniteRef: (el: HTMLElement | null) => void
  setInputRef: (el: any) => void
  input: string
  inputFocused: boolean
  pageList: any[]
  pageInfo: any
  skeleton: boolean
  modules: any[]
  currentTagId: string
  feeds: any[]
  feedShowName: string
  feedId: string
  feedsColumnType: string | number
  feedFetching: boolean
  feedEnding: boolean
  switchNavTrack: (item: any) => void
  onSearch: () => void
  gotoCollection: (id: string) => void
  gotoTags: (id: string) => void
  gotoBook: (id: string) => void
  getCurrentTagResources: (col: any) => any[]
  getCurrentShowTags: (col: any) => any[]
  handleTagClick: (tagId: string) => void
}>()

const emit = defineEmits<{
  (event: 'update:input', value: string): void
}>()

// 定义样式变量供 CSS v-bind 使用
const homeBgUrl = computed(() => `url(${props.config.theme.homeBg})`)
const progressGradient = computed(() => props.config.theme.tabActiveGradient)

// 处理输入更新
function handleInput(event: Event) {
  emit('update:input', (event.target as HTMLInputElement).value)
}
</script>

<template>
  <div :ref="setInfiniteRef" class="h-100dvh overflow-y-scroll">
    <div class="home-bg px-15px pb-20" :style="{ backgroundImage: `url(${config.theme.homeBg})` }">
      <!-- Tab -->
      <div v-show="!inputFocused" class="py-2.5 overflow-x-auto">
        <div class="flex items-center space-x-30px">
          <div v-for="item in pageList" :key="item.id">
            <div class="home-tab relative" :class="{ active: item.id === pageInfo.id }" @click="switchNavTrack(item)">
              <span class="text-14px whitespace-nowrap text-n-text-3">{{ item.showName }}</span>
            </div>
          </div>
        </div>
      </div>
      <!-- Search -->
      <div class="top-1.5 right-0 transition-all" :class="{ 'w-full!': inputFocused }">
        <div class="flex items-center space-x-5 relative z-10">
          <i-assets-icon-search class="w-4 h-4 absolute left-3 top-2.5 text-n-text-2" />
          <input
            :ref="setInputRef" :value="input" enterkeyhint="search"
            class="!ml0 flex-1 h-9 w-full pl-9 pr-4 rounded-full border-0 !outline-0 truncate caret-n-primary placeholder:text-14px bg-n-bg-card"
            :placeholder="config.texts.searchPlaceholder" @input="handleInput" @keyup.enter="onSearch"
          >
        </div>
        <div v-if="inputFocused" class="fixed inset-0 w-full h-100vh z-9 bg-n-bg-card" />
      </div>
      <!-- Modules -->
      <div v-if="skeleton" class="h-100dvh">
        <div class="pt-20vh flex justify-center">
          <BasicSpinner class="mx-auto" />
        </div>
        <p class="mt-4 text-center font-bold">
          {{ config.texts.loading }}
        </p>
      </div>
      <div v-else v-show="!inputFocused">
        <div v-for="col in modules" :key="col.id" class="mt-3.75">
          <template v-if="col.columnType === COLUMN_TYPE.SLIDE_BOOK">
            <ModuleSlideBook
              :module="col"
              :config="config"
              :goto-book="gotoBook"
              :goto-collection="gotoCollection"
            />
            <BizDownloadApp v-if="bannerShow" position="block" from="home" />
          </template>
          <!-- 新增2行3列的模块 -->
          <template v-else-if="col.columnType === COLUMN_TYPE.ROW_2_MUL_COL_3">
            <ModuleTags2x3
              :module="col"
              :config="config"
              :current-tag-id="currentTagId"
              :goto-book="gotoBook"
              :goto-tags="gotoTags"
              :get-current-tag-resources="getCurrentTagResources"
              :get-current-show-tags="getCurrentShowTags"
              :handle-tag-click="handleTagClick"
            />
          </template>
          <template v-else-if="col.columnType === COLUMN_TYPE.ROW_1_PLUS_COL_4">
            <ModuleFeatured1Plus4
              :module="col"
              :config="config"
              :goto-book="gotoBook"
              :goto-collection="gotoCollection"
            />
            <BizDownloadApp v-if="bannerShow" position="block" from="home" />
          </template>
          <template v-else-if="col.columnType === COLUMN_TYPE.ROW_2_MUL_COL_4">
            <ModuleGrid2x4
              :module="col"
              :config="config"
              :goto-book="gotoBook"
              :goto-collection="gotoCollection"
            />
            <BizDownloadApp v-if="bannerShow" position="block" from="home" />
          </template>
          <template v-else-if="col.columnType === COLUMN_TYPE.ROW_1_MUL_COL_4">
            <ModuleGrid1x4
              :module="col"
              :config="config"
              :goto-book="gotoBook"
              :goto-collection="gotoCollection"
            />
            <BizDownloadApp v-if="bannerShow" position="block" from="home" />
          </template>
          <template v-else-if="col.columnType === COLUMN_TYPE.ROW_3_MUL_COL_4">
            <ModuleRanking :module="col" :config="config" class="my-4 w-full" />
            <BizDownloadApp v-if="bannerShow" position="block" from="home" />
          </template>
          <template v-else-if="col.columnType === COLUMN_TYPE.SWIPER">
            <ModuleSwiper :module="col" :config="config" class="my-4 w-full" />
            <BizDownloadApp v-if="bannerShow" position="block" from="home" />
          </template>
        </div>
      </div>

      <!-- Feed -->
      <ModuleFeed
        v-if="feeds.length"
        :feeds="feeds"
        :feed-show-name="feedShowName"
        :feed-id="feedId"
        :feeds-column-type="feedsColumnType"
        :feed-fetching="feedFetching"
        :feed-ending="feedEnding"
        :config="config"
        :goto-book="gotoBook"
      />
    </div>

    <BasicTabbar v-show="!inputFocused" />
  </div>
</template>

<style lang="scss" scoped>
.home-bg {
  background-image: v-bind(homeBgUrl);
  background-repeat: no-repeat;
  background-position: top left;
  background-size: 100% auto;
}

.home-tab {
  cursor: pointer;
}

.home-tab.active {
  span {
    position: relative;
    line-height: 20px;
    font-size: 20px;
    color: var(--un-preset-theme-colors-n-text-1);
    font-weight: bold;
    z-index: 1;
  }

  i {
    position: absolute;
    left: 0;
    bottom: 0;
    margin-bottom: 2px;
    width: 100%;
    height: 6px;
    background-image: v-bind(progressGradient);
    border-radius: 3px;
    z-index: 0;
  }
}
</style>
